import { IconOtherPlay } from '@doltech/icons/IconOtherPlay';
import { Button } from '@doltech/ui/lib/figma/ButtonV2/ButtonV2';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Box } from '@doltech/ui/lib/figma/Common/Box';
import { QuestionTypeEnum, SkillEnum } from '@doltech/utils/lib/constants';
import { isBrowser } from '@doltech/utils/lib/platform';
import { smoothScroll } from '@doltech/utils/lib/smooth-scroll';
import cl from 'classnames';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { ERROR_NOT_SUPPORTED } from '../../../question-data-model/constants';
import { ExplanationWithSummaryDetail } from '../../components/ExplanationWithSummaryDetail';
import { CompletionNoHintAnswerResult } from '../../components/questions/CompletionNoHintAnswerResult';
import { DiagramLabelCompletionQuestionResult } from '../../components/questions/DiagramLabelCompletionResult';
import { FLowChartComplexResult } from '../../components/questions/FlowChartComplexResult';
import { FlowChartQuestionResult } from '../../components/questions/FlowChartQuestionResult';
import { MatchingEndingQuestionResult } from '../../components/questions/MatchingEndingQuestionResult';
import { MatchingFeatureNameQuestionResult } from '../../components/questions/MatchingFeatureNameQuestionResult';
import { MatchingHeadingQuestionResult } from '../../components/questions/MatchingHeadingQuestionResult';
import { MatchingParagraphQuestionResult } from '../../components/questions/MatchingParagraphQuestionResult';
import { MultipleQuestionResult } from '../../components/questions/MultipleQuestionResult';
import { SentenceCompleteQuestionResult } from '../../components/questions/SentenceCompleteQuestionResult';
import ShortAnswerResult from '../../components/questions/ShortAnswerResult';
import { SingleQuestionResult } from '../../components/questions/SingleQuestionResult';
import { TableCompletionQuestionResult } from '../../components/questions/TableCompletionQuestionResult';
import { TFNQuestionResult } from '../../components/questions/TFNQuestionResult';
import { YNNQuestionResult } from '../../components/questions/YNNQuestionResult';
import matchingQuestionMappingHelper from '../../helper/matchingQuestionMapping.helper';
import { Header, TestContentLayout, TestContentLayoutProps } from '../../layouts/TestContentLayout';
import { useTestResultStore } from '../../model/test-result.hook';
import { ResultListeningTestSectionInstance } from '../../model/TestSection/result-listening-test-section.store';
import { ResultReadingTestSectionInstance } from '../../model/TestSection/result-reading-test-section.store';
import { DragInQuestionResultMobile } from './DragInQuestionResultMobile';
import { DragOutQuestionResultMobile } from './DragOutQuestionResultMobile';

const SCRIPT_WRAPPER_MAX_HEIGHT = 216;
const SCRIPT_MARGIN_BOTTOM = 24;

const TestContentLayoutStyled = styled(TestContentLayout)`
  grid-template-columns: minmax(0, 1fr);
  display: grid;
  .section-test-header {
    padding: 0 16px;
  }
`;

interface TestContentMobileProps extends TestContentLayoutProps {}

const CurrentQuestionComp = () => {
  const store = useTestResultStore();
  const { goToQuestion } = store;

  if (store.currentQuestion) {
    const { no, uuid, type } = store.currentQuestion;
    const defaultProps = {
      key: `${no}${uuid}`,
      goToQuestion,
      testMode: store.testMode,
    };

    const currentSection = store.getCurrentTestSection() as ResultReadingTestSectionInstance;

    const questionsInGroup = currentSection.getQuestionsInGroup(store.currentGroupIndex);
    const matchingQuestionsInGroup = () =>
      questionsInGroup.map((question) =>
        matchingQuestionMappingHelper(type, question, currentSection.getMarkedBy())
      );

    if (type) {
      switch (type) {
        case QuestionTypeEnum.SINGLE_ANSWER:
          return (
            <SingleQuestionResult
              data={store.currentQuestion}
              questionsInGroup={questionsInGroup}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.TFN_ANSWER:
          return (
            <TFNQuestionResult
              data={store.currentQuestion}
              questionsInGroup={questionsInGroup}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.YNN_ANSWER:
          return (
            <YNNQuestionResult
              data={store.currentQuestion}
              questionsInGroup={questionsInGroup}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.MULTIPLE_ANSWER:
          return (
            <MultipleQuestionResult
              data={store.currentQuestion}
              questionsInGroup={questionsInGroup}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.MATCHING_PARAGRAPH:
          return (
            <MatchingParagraphQuestionResult
              data={store.currentQuestion}
              markedBy={currentSection.getMarkedBy()}
              questionsInGroup={matchingQuestionsInGroup()}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.MATCHING_HEADING:
          return (
            <MatchingHeadingQuestionResult
              data={store.currentQuestion}
              markedBy={currentSection.getMarkedBy()}
              questionsInGroup={matchingQuestionsInGroup()}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.MATCHING_ENDING:
          return (
            <MatchingEndingQuestionResult
              data={store.currentQuestion}
              questionsInGroup={matchingQuestionsInGroup()}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.MATCHING_FEATURE_NAME:
          return (
            <MatchingFeatureNameQuestionResult
              data={store.currentQuestion}
              questionsInGroup={matchingQuestionsInGroup()}
              {...defaultProps}
            />
          );

        case QuestionTypeEnum.SENTENCE_COMPLETE:
          return <SentenceCompleteQuestionResult data={store.currentQuestion} {...defaultProps} />;

        case QuestionTypeEnum.SHORT_ANSWER:
          return <ShortAnswerResult data={store.currentQuestion} {...defaultProps} />;

        case QuestionTypeEnum.NOTE_COMPLETION_WITH_HINT:
        case QuestionTypeEnum.NOTE_COMPLETION_NO_HINT:
          return <CompletionNoHintAnswerResult data={store.currentQuestion} {...defaultProps} />;

        case QuestionTypeEnum.FLOWCHART_ANSWER:
          return <FlowChartQuestionResult data={store.currentQuestion} {...defaultProps} />;

        case QuestionTypeEnum.DIAGRAM_LABEL_COMPLETION:
          return (
            <DiagramLabelCompletionQuestionResult {...defaultProps} data={store.currentQuestion} />
          );

        case QuestionTypeEnum.TABLE_COMPLETION:
          return <TableCompletionQuestionResult {...defaultProps} data={store.currentQuestion} />;

        case QuestionTypeEnum.DRAG_IN:
          return <DragInQuestionResultMobile {...defaultProps} data={store.currentQuestion} />;

        case QuestionTypeEnum.DRAG_OUT:
          return <DragOutQuestionResultMobile {...defaultProps} data={store.currentQuestion} />;

        case QuestionTypeEnum.FLOW_CHART_COMPLEX:
          return <FLowChartComplexResult {...defaultProps} data={store.currentQuestion} />;

        default:
          return (
            <div>
              {type} {ERROR_NOT_SUPPORTED}
            </div>
          );
      }
    }
  }
  return null;
};

const CurrentQuestion = observer(CurrentQuestionComp as React.FC);

const ListeningExplanationMain = styled.div`
  ${Box} {
    &:not(:last-child) {
      margin-bottom: 8px;
    }
  }

  .row-center {
    display: flex;
    align-items: center;
    .flex {
      flex: 1;
    }
  }
  .title {
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    color: ${colorsV2.black100};
  }
  .explanation-summary-detail {
    > div > div {
      margin: 0;
    }
  }

  #transcript-list {
    position: relative;
    padding: 12px 0;
    font-size: 16px;
    line-height: 24px;
    max-height: ${SCRIPT_WRAPPER_MAX_HEIGHT}px;
    overflow: auto;
    .highlight-transcript {
      margin-bottom: ${SCRIPT_MARGIN_BOTTOM}px;
      &:last-child {
        margin-bottom: 0;
      }
      &.active {
        font-weight: bold;
        color: ${colorsV2.primary100};
      }
    }
  }
`;

const ListeningExplanation = observer(() => {
  const store = useTestResultStore();
  const currentSection: ResultListeningTestSectionInstance = store?.testSections?.find(
    (_section, sectionIndex) => sectionIndex === store.currentSectionIndex
  );
  const currentQuestion = store?.currentQuestion;
  if (isNil(currentSection)) return null;
  const [highlightIndex, setHighlightIndex] = React.useState(-1);

  const { correction } = currentQuestion;

  React.useEffect(() => {
    if (correction?.explanation?.transcripts && currentSection?.audioManager?.progressInSeconds) {
      const currentTimeInSeconds = currentSection.audioManager.progressInSeconds;
      const findHighlightIndex = correction.explanation.transcripts.findIndex(
        (script) =>
          Math.round(currentTimeInSeconds) >= Math.floor(script.startTimeInSeconds) &&
          Math.round(currentTimeInSeconds) <= Math.ceil(script.endTimeInSeconds)
      );
      setHighlightIndex((oldHighlightIndex) => {
        if (findHighlightIndex !== oldHighlightIndex && findHighlightIndex >= 0 && isBrowser()) {
          const elmToScroll = document.getElementById(`transcript-${findHighlightIndex + 1}`);
          if (elmToScroll) {
            const elmOffsetTop =
              elmToScroll.offsetTop - elmToScroll.offsetHeight + SCRIPT_MARGIN_BOTTOM;
            smoothScroll(400, document.getElementById('transcript-list'), elmOffsetTop);
          }
        }
        return findHighlightIndex;
      });
    }
  }, [correction?.explanation?.transcripts, currentSection?.audioManager?.progressInSeconds]);

  return (
    <ListeningExplanationMain>
      <div className="section-test-header">
        <Header>Giải thích</Header>
      </div>

      <Box>
        <div className="row-center">
          <div className="title flex">Located Transcript</div>
          <Button.Secondary
            variant="outline"
            className="listen-button"
            iconLeft={<IconOtherPlay />}
            onClick={() => {
              store
                .getCurrentTestSection()
                .audioManager.playRange(
                  correction.explanation.startTimeInSeconds,
                  correction.explanation.endTimeInSeconds
                );
            }}
          >
            Listen from here
          </Button.Secondary>
        </div>

        <div id="transcript-list">
          {correction.explanation.transcripts.map((transcript, index) => {
            const { text, character } = transcript;
            return (
              <div
                id={`transcript-${index + 1}`}
                key={transcript.id}
                className={cl('highlight-transcript', { active: highlightIndex === index })}
              >
                {character && <strong>{character.name}:</strong>} {text}
              </div>
            );
          })}
        </div>
      </Box>
      <Box>
        <div className="title">Phân tích sâu hơn</div>
      </Box>
      <ExplanationWithSummaryDetail showTitle={false} />
    </ListeningExplanationMain>
  );
});

export const TestContentMobile = observer((props: TestContentMobileProps) => {
  const store = useTestResultStore();
  const currentSection = store?.testSections?.find(
    (_section, sectionIndex) => sectionIndex === store.currentSectionIndex
  );
  if (isNil(currentSection)) return null;

  switch (store.skill) {
    case SkillEnum.READING: {
      return (
        <TestContentLayoutStyled {...props}>
          <CurrentQuestion />
        </TestContentLayoutStyled>
      );
    }
    case SkillEnum.LISTENING: {
      return (
        <TestContentLayoutStyled {...props} renderExplanation={<ListeningExplanation />}>
          <CurrentQuestion />
        </TestContentLayoutStyled>
      );
    }
  }
  return null;
});
